import { useState } from "react";

import { t } from "../../../i18n";
import { cn } from "../../../lib/cn";
import { trpc } from "../../../lib/trpc";
import {
  Avatar,
  Button,
  Checkbox,
  Dialog,
  Field,
  FieldMessage,
  Input,
  Money,
  Select,
  Stack,
} from "../../ui";
import type { Expense, Member } from "../types";
import { labelForMember } from "../utils";

interface AdjustSplitDialogProps {
  /**
   * Scoped invalidate-key for `settlement.get`. Without it the mutation
   * would invalidate every settlement cache entry, racing against
   * optimistic updates ExpensesTable applies in parallel.
   */
  settlementId: string;
  expense: Expense;
  members: Member[];
  currency: string;
  onClose: () => void;
}

export function AdjustSplitDialog({
  settlementId,
  expense,
  members,
  currency,
  onClose,
}: AdjustSplitDialogProps) {
  const utils = trpc.useUtils();
  const setSplit = trpc.settlement.setSplit.useMutation();
  const updatePayer = trpc.settlement.updateExpense.useMutation();

  const total = Number.parseFloat(expense.amount);
  const [paidByUserId, setPaidByUserId] = useState<string>(expense.paidByUserId);
  // Inntastingsmodus for fordeling: "amount" (kr) eller "percent" (%). Lagring
  // skjer alltid i kr — prosent konverteres ved input/visning slik at presets
  // og remaining-beregning fortsatt fungerer pa total-belopet.
  const [splitMode, setSplitMode] = useState<"amount" | "percent">("amount");

  // Initialize from current splits: each member gets their existing share, or 0 if not included.
  const splits: Array<{ userId: string; share: string }> = expense.splits;
  const [shares, setShares] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    const existing = new Map(splits.map((s) => [s.userId, s.share]));
    for (const m of members) {
      map[m.userId] = existing.get(m.userId) ?? "0.00";
    }
    return map;
  });
  const [included, setIncluded] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    const inSplits = new Set(splits.map((s) => s.userId));
    for (const m of members) {
      map[m.userId] = inSplits.has(m.userId);
    }
    return map;
  });

  const includedIds = members.filter((m) => included[m.userId]).map((m) => m.userId);

  function setShare(userId: string, value: string) {
    setShares((prev) => ({ ...prev, [userId]: value }));
  }

  // Verdien som vises i input-feltet avhenger av modus: kr direkte fra shares,
  // eller prosent av total. Ved total=0 unngas div-by-zero.
  function inputDisplay(userId: string): string {
    const raw = shares[userId] ?? "0.00";
    if (splitMode === "amount") return raw;
    if (total === 0) return "0.00";
    const amount = Number.parseFloat(raw) || 0;
    return ((amount / total) * 100).toFixed(2);
  }

  // Ved input konverterer vi prosent → kr og lagrer altid i shares.
  function handleInput(userId: string, raw: string) {
    if (splitMode === "amount") {
      setShare(userId, raw);
      return;
    }
    const percent = Number.parseFloat(raw.replace(",", ".")) || 0;
    const amount = (percent / 100) * total;
    setShare(userId, amount.toFixed(2));
  }

  function toggleIncluded(userId: string, on: boolean) {
    setIncluded((prev) => ({ ...prev, [userId]: on }));
    if (!on) setShare(userId, "0.00");
  }

  function distributeEqually() {
    if (includedIds.length === 0) return;
    const perPerson = total / includedIds.length;
    const rounded = Math.floor(perPerson * 100) / 100;
    const remainder = +(total - rounded * includedIds.length).toFixed(2);
    setShares((prev) => {
      const next = { ...prev };
      for (const m of members) {
        if (included[m.userId]) {
          next[m.userId] = rounded.toFixed(2);
        } else {
          next[m.userId] = "0.00";
        }
      }
      // Put the rounding remainder on the first included member.
      const first = includedIds[0];
      if (first && remainder !== 0) {
        next[first] = (rounded + remainder).toFixed(2);
      }
      return next;
    });
  }

  function onlyPayerCovers() {
    setIncluded(() => {
      const map: Record<string, boolean> = {};
      for (const m of members) map[m.userId] = m.userId === paidByUserId;
      return map;
    });
    setShares(() => {
      const map: Record<string, string> = {};
      for (const m of members) {
        map[m.userId] = m.userId === paidByUserId ? total.toFixed(2) : "0.00";
      }
      return map;
    });
  }

  function payerCoversRest() {
    // Sum shares from every member except the payer, then make up the
    // difference on the payer.
    const others = members.filter((m) => m.userId !== paidByUserId);
    const sumOthers = others.reduce(
      (sum, m) => sum + (included[m.userId] ? Number.parseFloat(shares[m.userId] || "0") || 0 : 0),
      0,
    );
    const rest = +(total - sumOthers).toFixed(2);
    if (rest < -0.005) return; // others have already over-allocated; refuse
    setIncluded((prev) => ({ ...prev, [paidByUserId]: true }));
    setShares((prev) => ({
      ...prev,
      [paidByUserId]: Math.max(0, rest).toFixed(2),
    }));
  }

  /**
   * Skjev to-personers fordeling. Betaleren far `primaryShare`-prosent av
   * summen, den andre inkluderte far resten. Krever nayaktig 2 inkluderte —
   * ellers er presetet disabled i UI.
   */
  function distributeSkewed(primaryShare: number) {
    if (includedIds.length !== 2) return;
    const primary = includedIds.includes(paidByUserId) ? paidByUserId : includedIds[0];
    const secondary = includedIds.find((id) => id !== primary);
    if (!primary || !secondary) return;
    const primaryAmount = +((total * primaryShare) / 100).toFixed(2);
    const secondaryAmount = +(total - primaryAmount).toFixed(2);
    setShares((prev) => ({
      ...prev,
      [primary]: primaryAmount.toFixed(2),
      [secondary]: secondaryAmount.toFixed(2),
    }));
  }

  /** Nullstill alle andeler — inkluderte forblir inkluderte, bare belop = 0. */
  function clearShares() {
    setShares(() => {
      const map: Record<string, string> = {};
      for (const m of members) map[m.userId] = "0.00";
      return map;
    });
  }

  const canSkew = includedIds.length === 2;

  const allocated = members.reduce(
    (sum, m) => sum + (included[m.userId] ? Number.parseFloat(shares[m.userId] || "0") || 0 : 0),
    0,
  );
  const remaining = +(total - allocated).toFixed(2);
  const mismatch = Math.abs(remaining) > 0.005;
  const empty = includedIds.length === 0;

  async function submit() {
    const splitsPayload = includedIds.map((userId) => ({
      userId,
      share: (Number.parseFloat(shares[userId] || "0") || 0).toFixed(2),
    }));
    try {
      if (paidByUserId !== expense.paidByUserId) {
        await updatePayer.mutateAsync({ expenseId: expense.id, paidByUserId });
      }
      await setSplit.mutateAsync({ expenseId: expense.id, splits: splitsPayload });
      utils.settlement.get.invalidate({ settlementId });
      onClose();
    } catch {
      // Errors surface via mutation.error state on the buttons; no further handling needed.
    }
  }

  const isSubmitting = updatePayer.isPending || setSplit.isPending;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t.settlement.expenses.splitDialog.title}
      description={t.settlement.expenses.splitDialog.description}
      footer={
        <>
          <Button onClick={onClose} disabled={isSubmitting}>
            {t.common.cancel}
          </Button>
          <Button
            variant="primary"
            isLoading={isSubmitting}
            disabled={mismatch || empty}
            onClick={submit}
          >
            {t.settlement.expenses.splitDialog.submit}
          </Button>
        </>
      }
    >
      <Stack gap={4}>
        <Field label={t.settlement.expenses.splitDialog.paidByLabel}>
          <Select
            value={paidByUserId}
            onChange={(e) => setPaidByUserId(e.target.value)}
            disabled={isSubmitting}
          >
            {members.map((m) => {
              const lbl = labelForMember(m, members);
              return (
                <option key={m.userId} value={m.userId} title={lbl.tooltip}>
                  {lbl.name}
                </option>
              );
            })}
          </Select>
        </Field>

        <div className="flex items-center justify-between gap-3 rounded-md bg-ink-50 px-3 py-2">
          <span className="text-ink-700 text-sm">
            {t.settlement.expenses.splitDialog.totalLabel}
          </span>
          <div className="flex items-center gap-3">
            <Money amount={expense.amount} currency={currency} emphasize />
            <fieldset
              aria-label={t.settlement.expenses.splitDialog.modeGroupLabel}
              className="inline-flex shrink-0 rounded-md border border-ink-200 bg-surface p-0.5"
            >
              <button
                type="button"
                aria-pressed={splitMode === "amount"}
                onClick={() => setSplitMode("amount")}
                className={cn(
                  "inline-flex h-7 items-center justify-center rounded px-2 font-medium text-xs transition-colors",
                  splitMode === "amount"
                    ? "bg-ink-100 text-ink-900 dark:bg-ink-200"
                    : "text-ink-500 hover:text-ink-900",
                )}
              >
                {t.settlement.expenses.splitDialog.modeAmount}
              </button>
              <button
                type="button"
                aria-pressed={splitMode === "percent"}
                onClick={() => setSplitMode("percent")}
                className={cn(
                  "inline-flex h-7 items-center justify-center rounded px-2 font-medium text-xs transition-colors",
                  splitMode === "percent"
                    ? "bg-ink-100 text-ink-900 dark:bg-ink-200"
                    : "text-ink-500 hover:text-ink-900",
                )}
              >
                {t.settlement.expenses.splitDialog.modePercent}
              </button>
            </fieldset>
          </div>
        </div>

        <Stack gap={2}>
          {members.map((m) => {
            const on = !!included[m.userId];
            return (
              <div
                key={m.userId}
                className="flex items-center gap-3 rounded-md border border-ink-100 px-3 py-2"
              >
                <Checkbox
                  id={`split-include-${m.userId}`}
                  checked={on}
                  onChange={(e) => toggleIncluded(m.userId, e.target.checked)}
                  aria-label={t.settlement.expenses.splitDialog.includeLabel}
                />
                <label
                  htmlFor={`split-include-${m.userId}`}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-1"
                >
                  <Avatar name={m.displayName ?? m.email} size="sm" />
                  <span className="truncate text-ink-800 text-sm">{m.displayName ?? m.email}</span>
                </label>
                <div className="w-24 shrink-0 sm:w-28">
                  <Input
                    type="number"
                    step="0.01"
                    value={inputDisplay(m.userId)}
                    onChange={(e) => handleInput(m.userId, e.target.value)}
                    suffix={
                      splitMode === "amount"
                        ? t.settlement.expenses.splitDialog.shareSuffix
                        : t.settlement.expenses.splitDialog.percentSuffix
                    }
                    disabled={!on}
                  />
                </div>
              </div>
            );
          })}
        </Stack>

        <div>
          <p className="mb-1.5 font-medium text-ink-600 text-xs">
            {t.settlement.expenses.splitDialog.presetsHeading}
          </p>
          <div className="flex flex-wrap gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              iconLeft="arrow-right-left"
              onClick={distributeEqually}
              disabled={empty}
            >
              {includedIds.length === members.length
                ? t.settlement.expenses.splitDialog.equalSplitAll
                : t.settlement.expenses.splitDialog.equalSplitIncluded}
            </Button>
            <Button variant="secondary" size="sm" iconLeft="wallet" onClick={onlyPayerCovers}>
              {t.settlement.expenses.splitDialog.onlyPayerCovers}
            </Button>
            <Button variant="secondary" size="sm" iconLeft="check" onClick={payerCoversRest}>
              {t.settlement.expenses.splitDialog.payerCoversRest}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => distributeSkewed(70)}
              disabled={!canSkew}
              title={canSkew ? undefined : t.settlement.expenses.splitDialog.skewedRequiresTwo}
            >
              {t.settlement.expenses.splitDialog.split7030}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => distributeSkewed(60)}
              disabled={!canSkew}
              title={canSkew ? undefined : t.settlement.expenses.splitDialog.skewedRequiresTwo}
            >
              {t.settlement.expenses.splitDialog.split6040}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconLeft="x"
              onClick={clearShares}
              disabled={empty}
            >
              {t.settlement.expenses.splitDialog.clearShares}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-ink-700">{t.settlement.expenses.splitDialog.allocatedLabel}</span>
          <Money
            amount={allocated.toFixed(2)}
            currency={currency}
            className="font-medium text-ink-900"
          />
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-ink-700">{t.settlement.expenses.splitDialog.remainingLabel}</span>
          <Money
            amount={remaining.toFixed(2)}
            currency={currency}
            className={
              mismatch
                ? "font-semibold text-rose-600 dark:text-rose-400"
                : "font-semibold text-emerald-600 dark:text-emerald-400"
            }
          />
        </div>

        {empty && (
          <FieldMessage tone="error">{t.settlement.expenses.splitDialog.emptyError}</FieldMessage>
        )}
        {!empty && mismatch && (
          <FieldMessage tone="error">
            {splitMode === "percent" && total !== 0
              ? t.settlement.expenses.splitDialog.mismatchErrorPercent(
                  Math.abs((remaining / total) * 100).toFixed(2),
                )
              : t.settlement.expenses.splitDialog.mismatchError(Math.abs(remaining).toFixed(2))}
          </FieldMessage>
        )}
        {(setSplit.error || updatePayer.error) && (
          <FieldMessage tone="error">
            {setSplit.error?.message ?? updatePayer.error?.message}
          </FieldMessage>
        )}
      </Stack>
    </Dialog>
  );
}
